import React, { FC, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import Ghost from '@gravity-ui/icons/Ghost';
import Shield from '@gravity-ui/icons/Shield';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
// TODO: change all /blocks pages to new design
import BillingPage from 'client/blocks/Billing/BillingPage/BillingPage';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { AdminSettings } from 'client/blocks-redesign/AdminSettings/AdminSettings';
import { AliceAppAccountLinking } from 'client/blocks-redesign/AliceAppAccountLinking/AliceAppAccountLinking';
import { AliceAppDonationPage } from 'client/blocks-redesign/AliceAppDonationPage/AliceAppDonationPage';
import { AliceAppEditorPage } from 'client/blocks-redesign/AliceAppEditorPage/AliceAppEditorPage';
import { AliceAppMonitoringPage } from 'client/blocks-redesign/AliceAppMonitoring/AliceAppMonitoringPage';
import { AliceAppPromotionPage } from 'client/blocks-redesign/AliceAppPromotionPage/AliceAppPromotionPage';
import { AliceAppRatingPage } from 'client/blocks-redesign/AliceAppRatingPage';
import { AliceAppTestPage } from 'client/blocks-redesign/AliceAppTestPage/AliceAppTestPage';
import { IconSmall } from 'client/blocks-redesign/AppIcons/AppIcons';
import { DescribeAppInIcons } from 'client/blocks-redesign/DescribeAppInIcons/DescribeAppInIcons';
import { FeatureFlags } from 'client/blocks-redesign/FeatureFlags/FeatureFlags';
import { mapChannelToAppCard } from 'client/blocks-redesign/icons/mapChannelToAppCard';
import { SettingPageLayout } from 'client/blocks-redesign/Layout/Layout';
import { SideMenu } from 'client/blocks-redesign/Menu/Menu';
import { IMenuMobileItem, MenuMobile } from 'client/blocks-redesign/MenuMobile/MenuMobile';
import { NdaLink } from 'client/blocks-redesign/NdaLink/NdaLink';
import { Resources } from 'client/blocks-redesign/Resources/Resources';
import { AliceSkillsAppSettingsPage } from 'client/blocks-redesign/SettingsPage/AliceSkillAppSettingsPage';
import { SharingPage } from 'client/blocks-redesign/Sharing/SharingPage';
import { SmartHomeTestPage } from 'client/blocks-redesign/SmartHomeTestPage/SmartHomeTestPage';
import { DeploymentStatusMobile } from 'client/blocks-redesign/statuses/DeploymentStatus/DeploymentStatus';
import { SkillDeploymentStatus } from 'client/blocks-redesign/statuses/DeploymentStatus/SkillDeploymentStatus';
import { OperationStatus } from 'client/blocks-redesign/statuses/OperationStatus/OperationStatus';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import { useSkillMenu } from 'client/hooks/useSkillMenu';
import { moment } from 'client/lib/moment';
import { routes } from 'client/lib/routes';
import { isInDevelopment } from 'client/model/draft';
import { AliceSkill, Channel, SmartHome } from 'client/model/skill';
import { UserAgreement } from 'client/model/userAgreement';
import { getCanEditApp, getIsGlobalViewer } from 'client/utils';
import * as keyset from 'i18n/AppsIndex';

import styles from './AppPage.module.scss';
import { getAppHeader } from './utils';

interface Props {
    skill: AliceSkill | SmartHome;
    isDraft: boolean;
    userAgreements: UserAgreement[];
    userAgreementsDraft: UserAgreement[];
}

const t = i18n(keyset);

export const AliceSkillAppPage: FC<Props> = ({ skill, isDraft, userAgreements, userAgreementsDraft }) => {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const { menuItems, displaySkillMenu: display } = useSkillMenu({ skill, isDraft });

    const externalAsideLinks: IMenuMobileItem[] = [
        {
            value: routes.documentation.aliceSkills.feedback,
            content: t('feedback'),
        },
        {
            value: routes.documentation.index,
            content: t('docs'),
        },
    ];

    const disabled = !getCanEditApp(skill);

    return (
        <ErrorBoundary withHomepageLink>
            <SettingsPageContext.Provider
                value={{
                    isDraft,
                    isEditable: isDraft && isInDevelopment(skill),
                    hasUnsavedChanges,
                    setHasUnsavedChanges,
                }}
            >
                <SettingPageLayout>
                    <section className={styles.container}>
                        <div className={styles.leftColumn}>
                            <div>
                                <div className={styles.menuHeader}>
                                    <div className={styles.menuHeader__installableAppNameBlock}>
                                        <IconSmall
                                            imageUrl={skill.onAir ? skill.logo2?.url : skill.draft?.logo2?.url}
                                        />
                                        <h4 className={styles.menuHeader__header}>{getAppHeader(skill)}</h4>
                                    </div>
                                    <div className={styles.iconStatusBlock}>
                                        {__appConfig.user.isAdmin && <Shield className={styles.adminIcon} />}
                                        {getIsGlobalViewer() && <Ghost className={styles.viewerIcon} />}
                                        {mapChannelToAppCard?.[skill?.channel]?.icon}
                                    </div>
                                </div>
                                <SkillDeploymentStatus skill={skill} />
                                <SideMenu menuItems={menuItems} />
                            </div>
                            <div>
                                {externalAsideLinks.map((asideLink) => (
                                    <a
                                        key={asideLink.value}
                                        href={asideLink.value}
                                        className={clx(styles.link, styles.footerElement)}
                                    >
                                        {asideLink.content}
                                    </a>
                                ))}

                                <div className={styles.footerElement}>
                                    <NdaLink />
                                    {t('yandex-copyright', { year: moment().year() })}
                                </div>
                            </div>
                        </div>
                        <div className={styles.installableAppPageContent}>
                            <header className={styles.header__container}>
                                <div className={styles.header__statusContainer}>
                                    <div className={styles.header__commonStatusContainer}>
                                        <DescribeAppInIcons app={skill} />
                                        <div className={styles.header__operationStatusDesktop}>
                                            <OperationStatus isDraft={isDraft} />
                                        </div>
                                    </div>
                                    <DeploymentStatusMobile app={skill} />
                                </div>
                            </header>
                            <MenuMobile menuItems={menuItems} externalAsideLinks={externalAsideLinks}>
                                <Switch>
                                    <Route path={routes.app.skillPage(true, 'settings')}>
                                        <AliceSkillsAppSettingsPage
                                            skill={skill}
                                            isDraft={true}
                                            userAgreements={userAgreements}
                                            userAgreementsDraft={userAgreementsDraft}
                                            disabled={disabled}
                                        />
                                    </Route>
                                    <Route path={routes.app.skillPage(false, 'settings')}>
                                        <AliceSkillsAppSettingsPage
                                            skill={skill}
                                            isDraft={false}
                                            userAgreements={userAgreements}
                                            userAgreementsDraft={userAgreementsDraft}
                                            disabled={disabled}
                                        />
                                    </Route>
                                    <Route path={routes.app.installableAppSharing(skill.channel, skill.id)}>
                                        <SharingPage aliceApp={skill} />
                                    </Route>
                                    {display.test && skill.channel === Channel.AliceSkill && (
                                        <Route path={routes.app.skillPage(true, 'test')}>
                                            <AliceAppTestPage skill={skill} isDraft />
                                        </Route>
                                    )}
                                    {display.test && skill.channel === Channel.AliceSkill && (
                                        <Route path={routes.app.skillPage(false, 'test')}>
                                            <AliceAppTestPage skill={skill} isDraft={false} />
                                        </Route>
                                    )}
                                    {display.smartHomeTest && (
                                        <Route path={routes.app.skillPage(true, 'test')}>
                                            <SmartHomeTestPage skill={skill} isDraft />
                                        </Route>
                                    )}
                                    {display.smartHomeTest && (
                                        <Route path={routes.app.skillPage(false, 'test')}>
                                            <SmartHomeTestPage skill={skill} isDraft={false} />
                                        </Route>
                                    )}
                                    {display.promotion && (
                                        <Route path={routes.app.skillPage(false, 'promotion')}>
                                            <AliceAppPromotionPage skill={skill} />
                                        </Route>
                                    )}
                                    {display.rating && (
                                        <Route exact path={routes.app.skillRating()}>
                                            <AliceAppRatingPage />
                                        </Route>
                                    )}
                                    {display.resources && skill.channel === Channel.AliceSkill && (
                                        <Route
                                            path={routes.app.skillResources('')}
                                            render={(props) => (
                                                <Resources {...props} skill={skill} disabled={disabled} />
                                            )}
                                        />
                                    )}

                                    {display.billing && (
                                        <Route
                                            path={routes.app.skillPage(false, 'billing')}
                                            // TODO: change to new design
                                            render={(props) => (
                                                <BillingPage {...props} skill={skill} disabled={disabled} />
                                            )}
                                        />
                                    )}
                                    {display.monitoring && (
                                        <Route path={routes.app.skillPage(false, 'monitoring')}>
                                            <AliceAppMonitoringPage skill={skill} />
                                        </Route>
                                    )}
                                    {display.donation && (
                                        <Route path={routes.app.skillPage(false, 'donation')}>
                                            <AliceAppDonationPage disabled={disabled} />
                                        </Route>
                                    )}
                                    {display.sharing && (
                                        <Route path={routes.app.skillPage(false, 'sharing')}>
                                            <SharingPage aliceApp={skill} />
                                        </Route>
                                    )}
                                    {display.editorFields && (
                                        <Route path={routes.app.skillPage(false, 'editing')}>
                                            <AliceAppEditorPage disabled={disabled} />
                                        </Route>
                                    )}
                                    {display.featureFlags && (
                                        <Route path={routes.app.skillPage(false, 'feature-flags', skill.id)}>
                                            <FeatureFlags />
                                        </Route>
                                    )}
                                    {display.adminSettings && (
                                        <Route path={routes.app.skillPage(false, 'admin-settings', skill.id)}>
                                            <AdminSettings disabled={disabled} />
                                        </Route>
                                    )}
                                    {display.authorization && (
                                        <Route path={routes.app.skillPage(false, 'authorization', skill.id)}>
                                            <AliceAppAccountLinking skill={skill} disabled={disabled} />
                                        </Route>
                                    )}
                                    <Redirect
                                        from={routes.app.installableApp(Channel.AliceSkill)}
                                        to={routes.app.appPage(Channel.AliceSkill, true, 'settings')}
                                    />
                                </Switch>
                            </MenuMobile>
                            <div className={styles.footerMobile}>
                                <NdaLink />
                                {t('yandex-copyright', { year: moment().year() })}
                            </div>
                        </div>
                    </section>
                </SettingPageLayout>
            </SettingsPageContext.Provider>
        </ErrorBoundary>
    );
};
